import { faCheck, faTag } from '@fortawesome/free-solid-svg-icons';
import {
  faChevronDown,
  faChevronRight,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox } from '@headlessui/react';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { DotsVerticalIcon, PlusIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { by } from '~/helpers/filter';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { FloatingMenu, FloatingMenuItem } from '../ui/FloatingMenu';
import type { Tag } from './tags';

type Props = {
  items: Tag[];
  searchKeys: (keyof Tag)[];
  placeholder?: string;
  value: Tag[];
  onChange: (value: Tag[]) => void;
  onCreate?: ((tag?: Tag) => void) | null;
  onDelete: (tag: Tag) => void;
};

/**
 * Combobox with static, always visible list of options. Use inside a popover, dialog, command palette etc.
 */
export function TagCombolist({
  items,
  searchKeys,
  placeholder = '',
  value,
  onChange,
  onCreate,
  onDelete,
}: Props) {
  const [query, setQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set()
  );
  const { isMobile } = useBreakpoint();

  const searchResult =
    query === '' ? items : items.filter(by(query, searchKeys));

  // Group tags: categorized first (sorted by category name), uncategorized last (sorted by name)
  const groupedTags = useMemo(() => {
    const categorized = searchResult.filter((tag) => tag.category);
    const uncategorized = searchResult.filter((tag) => !tag.category);

    // Sort categorized by category then name, uncategorized by name
    const sortedCategorized = _.orderBy(categorized, ['category', 'name']);
    const sortedUncategorized = _.orderBy(uncategorized, ['name']);

    return [...sortedCategorized, ...sortedUncategorized];
  }, [searchResult]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  function handleChange(newValue: Tag[]) {
    if (
      onCreate &&
      newValue.length > 0 &&
      newValue[newValue.length - 1] === null
    ) {
      onCreate({
        name: query,
        description: '',
        category: '',
        entityType: '',
        colour: '81848F',
      } as Tag);
      return;
    }

    onChange(newValue);
  }

  return (
    <Combobox
      as='div'
      className='flex min-h-0 flex-1 flex-col'
      value={value}
      onChange={handleChange}
      multiple={true}
      by={(a, b) => a?.id === b?.id}
    >
      <div className='sticky top-0 z-[60] rounded-t-md bg-white p-2'>
        {items.length ? (
          <div className='x-3 flex items-center rounded-full bg-grey-3 px-4 py-2'>
            <FontAwesomeIcon
              icon={faSearch}
              className='mr-3 text-sm text-grey-40'
            />
            <Combobox.Input
              className='flex-1 bg-transparent text-base outline-none'
              type='text'
              placeholder={
                placeholder
                  ? `Search ${placeholder.toLowerCase()}...`
                  : 'Search...'
              }
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
          </div>
        ) : (
          <div className='ml-1 text-base text-tertiary'>
            Tags selector is currently empty
          </div>
        )}
      </div>
      <Combobox.Options
        static
        className='flex min-h-0 flex-1 scroll-py-3 flex-col overflow-y-auto'
      >
        {onCreate && (
          <Combobox.Option
            value={null}
            className={({ active }) =>
              classNames(
                'flex cursor-pointer select-none items-center justify-between py-2 text-gray-900 active:bg-shade-12',
                active && 'bg-shade-10'
              )
            }
            key={'create'}
          >
            <div className='ml-2 flex items-center gap-2 truncate whitespace-nowrap'>
              <PlusIcon className='h-5 text-gray-900' aria-hidden='true' />
              <div className='text-base'>New Tag</div>
            </div>
          </Combobox.Option>
        )}

        <TagItems tags={groupedTags} />
      </Combobox.Options>
    </Combobox>
  );

  function TagItems({ tags }: { tags: Tag[] }) {
    const renderedCategories: string[] = [];

    // Check if we have any uncategorized tags
    const hasUncategorized = tags.some((tag) => !tag.category);

    return (
      <>
        {tags.map((tag) => {
          const isNewCategory =
            tag.category && !renderedCategories.includes(tag.category);
          const isCollapsed = tag.category
            ? collapsedCategories.has(tag.category)
            : false;

          // Skip rendering tag if its category is collapsed
          if (tag.category && !isNewCategory && isCollapsed) {
            return null;
          }

          // Build tag menu items for FloatingMenu
          const tagMenuItems: FloatingMenuItem[] = [];
          if (onCreate) {
            tagMenuItems.push({
              key: 'edit',
              icon: <PencilIcon className='h-5 w-5' aria-hidden='true' />,
              label: 'Edit Tag',
              onClick: () => onCreate?.(tag),
            });
          }
          tagMenuItems.push({
            key: 'delete',
            icon: <TrashIcon className='h-5 w-5' aria-hidden='true' />,
            label: 'Delete',
            onClick: () => onDelete(tag),
          });

          const tagItem = (
            <div key={tag.id}>
              {/* Category header with collapse toggle */}
              {isNewCategory && (
                <div className='mx-2 mb-1 mt-2 flex items-center'>
                  <button
                    type='button'
                    className='flex items-center gap-1.5 text-sm font-medium text-secondary hover:text-primary'
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleCategory(tag.category!);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <FontAwesomeIcon
                      icon={isCollapsed ? faChevronRight : faChevronDown}
                      className='h-2.5 w-2.5 text-grey-40'
                    />
                    <span>{tag.category}</span>
                  </button>
                </div>
              )}

              {/* Uncategorized section header */}
              {!tag.category &&
                hasUncategorized &&
                !renderedCategories.includes('__uncategorized__') && (
                  <div className='mx-2 mt-3 border-t border-grey-10 pt-2'>
                    <span className='text-sm font-medium text-grey-40'>
                      Uncategorized
                    </span>
                  </div>
                )}

              {/* Tag item - skip if category is collapsed */}
              {!isCollapsed && (
                <Combobox.Option key={tag.id} value={tag}>
                  {({ active, selected, disabled }) => (
                    <div
                      className={classNames(
                        'flex min-h-8 cursor-default select-none items-center pl-2 active:bg-shade-12',
                        !tag.description && 'py-1',
                        active && 'bg-shade-10',
                        selected && 'bg-shade-15',
                        disabled && 'opacity-75'
                      )}
                    >
                      <div className='mr-auto flex flex-col items-start truncate whitespace-nowrap py-0.5'>
                        <div className='ml-0.5 flex items-center'>
                          <FontAwesomeIcon
                            className='mr-2.5'
                            style={{ color: `#${tag.colour}` }}
                            icon={faTag}
                          />
                          <span className='truncate text-base'>{tag.name}</span>
                        </div>
                        {tag.description && (
                          <div className='align-left ml-6 truncate text-left text-sm text-tertiary'>
                            {tag.description}
                          </div>
                        )}
                      </div>
                      {selected && (
                        <FontAwesomeIcon
                          className='mx-3 text-brand'
                          icon={faCheck}
                          fontSize='20px'
                        />
                      )}
                      {onCreate && (
                        <div
                          className={classNames(
                            'transition-opacity',
                            isMobile || active ? 'opacity-100' : 'opacity-0'
                          )}
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          onPointerUp={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onMouseUp={(e) => e.stopPropagation()}
                        >
                          <FloatingMenu
                            items={tagMenuItems}
                            buttonText={
                              <DotsVerticalIcon className='h-4 w-4' />
                            }
                            buttonClassName={(isOpen) =>
                              classNames(
                                'flex h-8 w-8 items-center justify-center rounded-full',
                                isOpen
                                  ? 'bg-shade-15 text-primary'
                                  : 'hover:bg-white/70 hover:text-primary text-secondary'
                              )
                            }
                            usePortal={false}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Combobox.Option>
              )}
            </div>
          );

          if (tag.category && !renderedCategories.includes(tag.category)) {
            renderedCategories.push(tag.category);
          }

          // Track uncategorized section
          if (
            !tag.category &&
            !renderedCategories.includes('__uncategorized__')
          ) {
            renderedCategories.push('__uncategorized__');
          }

          return tagItem;
        })}
      </>
    );
  }
}
